/**
 * Save a name onto the signed-in account, from wherever it is asked for.
 *
 * The sell page and the account page ask for a first name when the account has
 * none. Kept apart from `person-name.ts`, which the server imports: this one runs
 * in the browser and reports through the client error tracker.
 *
 * The caller refreshes the NextAuth session afterwards — the profile is seeded
 * from the JWT, so a saved name is invisible to the UI until it does.
 */
import { trackError, ErrorSeverity } from './error-tracking';
import { splitFullName } from './person-name';

export type SaveNameError = 'name_required' | 'save_failed' | 'network_error';

export interface SaveNameResult {
  success: boolean;
  error?: SaveNameError;
  firstName?: string;
}

/** Split one "Your name" field into first and last, and write both. */
export async function saveProfileName(fullName: string): Promise<SaveNameResult> {
  const { firstName, lastName } = splitFullName(fullName);
  if (!firstName) return { success: false, error: 'name_required' };

  try {
    const response = await fetch('/api/user/profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName }),
    });
    const data = await response.json();
    if (response.ok && data.success) return { success: true, firstName };
    return { success: false, error: 'save_failed' };
  } catch (e) {
    trackError('Failed to save a name onto the account', ErrorSeverity.WARNING, {
      component: 'save-profile-name',
      action: 'saveProfileName',
      // Only fetch (TypeError, AbortError) and res.json (SyntaxError) throw here: always Errors.
      extra: { error: (e as Error).message },
    });
    return { success: false, error: 'network_error' };
  }
}
